import { IObservableArray, makeAutoObservable, observable } from "mobx";
import { AccountLeadAttributionAnalyticsApi } from "api/account-lead-attribution-analytics.api";
import {
  TLeadAttributionPerson,
  TLeadAttributionPeopleMeta,
  TLeadAttributionByItem,
  TItemStatsSummary,
  TLeadAttributionByOfficeStatsDto,
  TLeadAttributionBySourceStatsDto,
  TLeadAttributionByUtmCampaignStatsDto,
  TLeadAttributionByJourneyStatsDto,
  TLeadAttributionStatItemByTypeByGroup,
} from "types/account-lead-attribution-analyticis.type";
import {
  LeadAttributionGroupedEnum,
  LeadAttributionStatusEnum,
  LeadAttributionTypesEnum,
} from "enums/account-lead-attribution.enum";
import { AccountAnalyticsLeadAttributionStore } from "./AccountAnalyticsLeadAttribution.store";
import { getStatusesForGroup } from "utils/account-lead-attribution-analytics.utils";

const DEFAULT_PAGE_SIZE = 20;

const leadAttributionAnalyticsApi = new AccountLeadAttributionAnalyticsApi();

const getDefaultMeta = (): TLeadAttributionPeopleMeta => {
  return {
    count: 0,
    afterKey: -1,
  };
};

export enum LeadAttributionPeopleFilterTypes {
  EVENT_TYPE = "eventTypes",
  OFFICE = "offices",
  SOURCE = "sources",
  UTM_CAMPAIGN = "utmCampaign",
  JOURNEY_NAME = "journeyName",
}

export type TLeadAttributionPeopleFilters = {
  [LeadAttributionPeopleFilterTypes.EVENT_TYPE]: LeadAttributionStatusEnum[];
  [LeadAttributionPeopleFilterTypes.OFFICE]: number[];
  [LeadAttributionPeopleFilterTypes.SOURCE]: string[];
  [LeadAttributionPeopleFilterTypes.UTM_CAMPAIGN]: string[];
  [LeadAttributionPeopleFilterTypes.JOURNEY_NAME]: string[];
};

const filterInitialState: TLeadAttributionPeopleFilters = {
  eventTypes: [],
  offices: [],
  sources: [],
  utmCampaign: [],
  journeyName: [],
};

export class AccountAnalyticsLeadAttributionPeopleStore {
  private readonly accountId: number;
  private leadAttributionPeople: IObservableArray<TLeadAttributionPerson>;
  private accountAnalyticsLeadAttributionStore: AccountAnalyticsLeadAttributionStore;
  private filterData: TLeadAttributionPeopleFilters;
  private metaData: TLeadAttributionPeopleMeta;
  public isFetchingInProgress: boolean;
  public selectedStatItemByType: TLeadAttributionStatItemByTypeByGroup | null;

  constructor(
    accountId: number,
    accountAnalyticsLeadAttributionStore: AccountAnalyticsLeadAttributionStore
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.leadAttributionPeople = observable.array<TLeadAttributionPerson>();
    this.accountId = accountId;
    this.metaData = getDefaultMeta();
    this.filterData = filterInitialState;
    this.accountAnalyticsLeadAttributionStore = accountAnalyticsLeadAttributionStore;
    this.isFetchingInProgress = false;
    this.selectedStatItemByType = null;
  }

  get people() {
    return this.leadAttributionPeople.slice();
  }

  get meta() {
    return this.metaData;
  }

  get shouldLoadNextBatch() {
    return !(this.meta.afterKey === null);
  }

  get filters() {
    return { ...this.filterData };
  }

  public async toggleEventTypeFilter(status: LeadAttributionStatusEnum) {
    const index = this.filters[
      LeadAttributionPeopleFilterTypes.EVENT_TYPE
    ].indexOf(status);
    if (index > -1) {
      this.filters.eventTypes.splice(index, 1);
    } else {
      this.filters.eventTypes.push(status);
    }

    await this.applyFiltersAndReload();
  }

  get isLeadAttributionStatGroupSelected() {
    return !!this.selectedStatItemByType;
  }

  private setLeadAttributionPeople(data: TLeadAttributionPerson[]) {
    this.leadAttributionPeople.push(...data);
  }

  private setMeta(meta: TLeadAttributionPeopleMeta) {
    this.metaData = { ...meta };
  }

  private setEventGroupFilter(group: LeadAttributionGroupedEnum) {
    const statusesForGroup = getStatusesForGroup(group);
    this.filters.eventTypes.push(...statusesForGroup);
  }

  private setOfficeFilter(item: TItemStatsSummary<TLeadAttributionByItem>) {
    const data = item as TLeadAttributionByOfficeStatsDto;
    this.filters.offices.push(data.officeId);
  }

  private setSourceFilter(item: TItemStatsSummary<TLeadAttributionByItem>) {
    const data = item as TLeadAttributionBySourceStatsDto;
    this.filters.sources.push(data.source);
  }

  private setUtmCampaignFilter(
    item: TItemStatsSummary<TLeadAttributionByItem>
  ) {
    const data = item as TLeadAttributionByUtmCampaignStatsDto;
    this.filters.utmCampaign.push(data.utmCampaign);
  }

  private setJourneyFilter(item: TItemStatsSummary<TLeadAttributionByItem>) {
    const data = item as TLeadAttributionByJourneyStatsDto;
    this.filters.journeyName.push(data.journeyName);
  }

  public setSelectedStatItem(stat: TLeadAttributionStatItemByTypeByGroup) {
    this.reset();
    this.selectedStatItemByType = stat;
    this.setFilter(stat.type, stat.item, stat.group);
  }

  public setFilter(
    type: LeadAttributionTypesEnum,
    item: TItemStatsSummary<TLeadAttributionByItem>,
    group: LeadAttributionGroupedEnum
  ) {
    const filterMap = {
      [LeadAttributionTypesEnum.OFFICE]: this.setOfficeFilter,
      [LeadAttributionTypesEnum.SOURCE]: this.setSourceFilter,
      [LeadAttributionTypesEnum.CAMPAIGN]: this.setUtmCampaignFilter,
      [LeadAttributionTypesEnum.JOURNEY]: this.setJourneyFilter,
    };

    filterMap[type]?.(item);
    this.setEventGroupFilter(group);
  }

  public reset() {
    this.leadAttributionPeople.clear();
    this.metaData = getDefaultMeta();
    this.filterData = filterInitialState;
    this.selectedStatItemByType = null;
  }

  public async applyFiltersAndReload() {
    this.leadAttributionPeople.clear();
    this.metaData = getDefaultMeta();
    await this.fetchLeadAttributionPeople();
  }

  public async fetchLeadAttributionPeople() {
    if (!this.shouldLoadNextBatch) return;
    if (this.isFetchingInProgress) return;

    const dateRange = this.accountAnalyticsLeadAttributionStore
      .accountAnalyticsFiltersStore.dateRange;

    try {
      this.isFetchingInProgress = true;
      const {
        data,
        meta,
      } = await leadAttributionAnalyticsApi.fetchLeadAttributionPeople(
        this.accountId,
        this.filters,
        this.meta.afterKey,
        dateRange,
        DEFAULT_PAGE_SIZE
      );
      this.setLeadAttributionPeople(data);
      this.setMeta(meta);
      this.isFetchingInProgress = false;
    } catch (error) {
      this.isFetchingInProgress = false;
      throw error;
    }
  }
}
